/**
 * Land and floor area units. The executive types an area in the unit he
 * prefers; it is stored as "<number> <unit>" (e.g. "0.2500 acres") and can be
 * printed in any other unit. Conversions go through square metres, exactly.
 *
 *   1 acre = 100 cents = 4,840 sq yd = 43,560 sq ft = 4,046.8564224 sq m
 *   1 hectare = 10,000 sq m;  1 sq yd = 9 sq ft = 0.83612736 sq m
 *   1 acre = 40 guntas (1 gunta = 121 sq yd);  1 ankanam = 72 sq ft = 8 sq yd
 */
import { AREA_UNITS, type AreaUnit } from './ast';
import { div, groupIndianDigits, mul, parseDec, toFixed, type Dec } from './decimal';

const SQ_M: Record<AreaUnit, string> = {
  acres: '4046.8564224',
  cents: '40.468564224',
  hectares: '10000',
  sq_yd: '0.83612736',
  sq_m: '1',
  sq_ft: '0.09290304',
  guntas: '101.17141056',
  ankanams: '6.68901888',
};

export const UNIT_LABEL: Record<AreaUnit, string> = {
  acres: 'Acres', cents: 'Cents', hectares: 'Hectares', sq_yd: 'Sq. Yards', sq_m: 'Sq. Metres', sq_ft: 'Sq. Feet',
  guntas: 'Guntas', ankanams: 'Ankanams',
};

/** Decimals shown for each unit. */
export const UNIT_DECIMALS: Record<AreaUnit, number> = {
  acres: 4, cents: 2, hectares: 4, sq_yd: 2, sq_m: 2, sq_ft: 2, guntas: 2, ankanams: 2,
};

export interface AreaValue { amount: Dec; unit: AreaUnit; text: string }

const AREA_TEXT = new RegExp(`^\\s*([\\d,]{1,20}(?:\\.\\d{1,8})?)\\s+(${AREA_UNITS.join('|')})\\s*$`);

/** "0.2500 acres" -> value. Null when it is not a number followed by a known unit. */
export function parseArea(raw: string): AreaValue | null {
  const m = AREA_TEXT.exec(raw);
  if (!m) return null;
  const amount = parseDec(m[1]!);
  if (amount === null || amount < 0n) return null;
  return { amount, unit: m[2] as AreaUnit, text: m[1]!.replace(/,/g, '') };
}

export function areaText(amount: string, unit: AreaUnit): string {
  return `${amount.trim()} ${unit}`;
}

export function convertArea(amount: Dec, from: AreaUnit, to: AreaUnit): Dec {
  if (from === to) return amount;
  const sqm = mul(amount, parseDec(SQ_M[from])!);
  return div(sqm, parseDec(SQ_M[to])!)!;
}

/** Just the number, e.g. "0.2500" or "1,011.71". */
export function areaNumber(amount: Dec, unit: AreaUnit): string {
  return groupIndianDigits(toFixed(amount, UNIT_DECIMALS[unit]));
}

/**
 * How an area prints in a deed. Acres follow the Andhra convention from the
 * owner's sample deed: "Acres 0.2500 Cents" (four decimals = acres and cents).
 */
export function formatArea(amount: Dec, unit: AreaUnit): string {
  const n = areaNumber(amount, unit);
  switch (unit) {
    case 'acres': return `Acres ${n} Cents`;
    case 'cents': return `${n} Cents`;
    case 'hectares': return `${n} Hectares`;
    case 'sq_yd': return `${n} Sq. Yds.`;
    case 'sq_m': return `${n} Sq. Mts.`;
    case 'sq_ft': return `${n} Sq. Ft.`;
    case 'guntas': return `${n} Guntas`;
    case 'ankanams': return `${n} Ankanams`;
  }
}
